import React from 'react';
import { Plus, Package, Settings, TrendingUp } from 'lucide-react';
import TechStackTable from '../components/TechStackTable';
import { PRODUCT_SUGGESTIONS } from '../../config/cisaMaturityModel';

/**
 * PillarTechnologiesTab - Tech stack table filtered to current pillar
 *
 * Enhanced with:
 * - Config status column showing configuration completeness
 * - Maturity contribution indicator
 */
const PillarTechnologiesTab = ({
  pillarCode,
  pillar,
  functions,
  assessments,
  productAdoptions,
  configCompletions = {},
  derivedScores = {},
  onAdoptProduct,
  onUpdateProductStatus,
  onRemoveProduct,
  onViewConfig
}) => {
  // Count products for this pillar
  const pillarProductCount = functions.reduce((count, fn) => {
    return count + (productAdoptions[fn.code]?.length || 0);
  }, 0);

  // Calculate config statistics
  const configStats = functions.reduce(
    (stats, fn) => {
      const fnConfigs = configCompletions[fn.code] || {};
      const products = productAdoptions[fn.code] || [];

      for (const product of products) {
        if (product.status === 'DEPLOYED') {
          stats.deployed++;
          const productConfigs = fnConfigs[product.productName] || {};
          const steps = Object.values(productConfigs);
          const completed = steps.filter(s => s === true).length;
          const total = steps.length;

          if (total > 0) {
            if (completed === total) {
              stats.fullyConfigured++;
            } else if (completed > 0) {
              stats.partiallyConfigured++;
            } else {
              stats.notConfigured++;
            }
          }
        }
      }
      return stats;
    },
    { deployed: 0, fullyConfigured: 0, partiallyConfigured: 0, notConfigured: 0 }
  );

  // Get suggested products for functions without products
  const suggestionsForUnmetFunctions = [];
  for (const fn of functions) {
    const currentProducts = productAdoptions[fn.code] || [];
    if (currentProducts.length === 0) {
      const currentLevel = assessments[fn.code] || 'TRADITIONAL';
      const nextLevel = currentLevel === 'OPTIMAL' ? null :
        currentLevel === 'ADVANCED' ? 'OPTIMAL' :
          currentLevel === 'INITIAL' ? 'ADVANCED' : 'INITIAL';

      if (nextLevel && PRODUCT_SUGGESTIONS[fn.code]?.[nextLevel]) {
        suggestionsForUnmetFunctions.push({
          functionCode: fn.code,
          functionName: fn.name,
          targetLevel: nextLevel,
          suggestions: PRODUCT_SUGGESTIONS[fn.code][nextLevel].slice(0, 3)
        });
      }
    }
  }

  return (
    <div className="cisa-pillar-technologies">
      {/* Header */}
      <div className="cisa-pillar-technologies-header">
        <div className="cisa-pillar-technologies-title">
          <Package size={20} style={{ color: pillar.color }} />
          <div>
            <h3>Technology Stack</h3>
            <p>{pillarProductCount} products tracked for {pillar.name}</p>
          </div>
        </div>
      </div>

      {/* Config status summary */}
      {configStats.deployed > 0 && (
        <div className="cisa-pillar-technologies-config-summary">
          <div className="cisa-pillar-technologies-config-stat cisa-pillar-technologies-config-stat--success">
            <Settings size={14} />
            <span className="cisa-pillar-technologies-config-stat-value">{configStats.fullyConfigured}</span>
            <span className="cisa-pillar-technologies-config-stat-label">Fully Configured</span>
          </div>
          <div className="cisa-pillar-technologies-config-stat cisa-pillar-technologies-config-stat--warning">
            <Settings size={14} />
            <span className="cisa-pillar-technologies-config-stat-value">{configStats.partiallyConfigured}</span>
            <span className="cisa-pillar-technologies-config-stat-label">Partial</span>
          </div>
          <div className="cisa-pillar-technologies-config-stat cisa-pillar-technologies-config-stat--error">
            <Settings size={14} />
            <span className="cisa-pillar-technologies-config-stat-value">{configStats.notConfigured}</span>
            <span className="cisa-pillar-technologies-config-stat-label">Not Configured</span>
          </div>
        </div>
      )}

      {/* Tech stack table */}
      <TechStackTable
        productAdoptions={productAdoptions}
        configCompletions={configCompletions}
        derivedScores={derivedScores}
        pillarFilter={pillarCode}
        onUpdateProduct={onUpdateProductStatus}
        onRemoveProduct={onRemoveProduct}
        onAddProduct={onAdoptProduct}
        onViewConfig={onViewConfig}
        showConfigStatus={true}
      />

      {/* Suggestions for functions without products */}
      {suggestionsForUnmetFunctions.length > 0 && (
        <div className="cisa-pillar-technologies-suggestions">
          <h4>Suggested Products for Unmet Functions</h4>
          <p className="cisa-pillar-technologies-suggestions-desc">
            These functions don't have any products tracked yet. Consider adding these to improve coverage:
          </p>

          <div className="cisa-pillar-technologies-suggestion-list">
            {suggestionsForUnmetFunctions.map(item => (
              <div key={item.functionCode} className="cisa-pillar-technologies-suggestion-item">
                <div className="cisa-pillar-technologies-suggestion-header">
                  <span className="cisa-pillar-technologies-suggestion-function">
                    {item.functionName}
                  </span>
                  <span className="cisa-pillar-technologies-suggestion-target">
                    For {item.targetLevel.charAt(0) + item.targetLevel.slice(1).toLowerCase()}
                  </span>
                </div>
                <div className="cisa-pillar-technologies-suggestion-products">
                  {item.suggestions.map((product, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="cisa-pillar-technologies-add-product-btn"
                      onClick={() => onAdoptProduct?.(item.functionCode, product, 'EVALUATING')}
                    >
                      <Plus size={12} />
                      {product}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PillarTechnologiesTab;
